import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import MainContainer from "../../layout/MainContainer";
import {
  Button,
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditText from "../../components/EditText";
import {
  arabicInputProps,
  withArabicFieldClass,
} from "../../utils/arabicField";
import PhoneInput from "react-phone-input-2";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import "react-phone-input-2/lib/bootstrap.css";
import { useLazyGetCmsQuery, usePostCmsMutation } from "../../services/cms";
import useAuth from "../../hooks/useAuth";
import { Loader, showError, showToast } from "../../constants";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody, AppSettingRow } from "../../types/General";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailIcon from "@mui/icons-material/Email";
import useTranslation from "../../hooks/Translation";
import moment from "moment";
import {
  useAddLinksMutation,
  useDeleteLinksMutation,
  useLazyGetAllLinksQuery,
} from "../../services/main";
import WarnModal from "../../components/WarnModal";
import PublicLinksCard from "../../components/PublicLinksCard";
import { UploadMedia } from "../../utils/mediaUpload";
import { resolveMediaUrl } from "../../utils/mediaUrl";

const DEFAULT_APP_SETTINGS: AppSettingRow[] = [
  {
    label: "user",
    platform: "ios",
    version: "1.0",
    appUpdate: false,
    forceUpdate: false,
    maintenance: false,
    storeUrl: "",
    maintenanceMessage: "",
    lastUpdated: null,
  },
  {
    label: "user",
    platform: "android",
    version: "1.0",
    appUpdate: false,
    forceUpdate: false,
    maintenance: false,
    storeUrl: "",
    maintenanceMessage: "",
    lastUpdated: null,
  },
];

const normalizeAppSettings = (rows?: any): AppSettingRow[] => {
  if (Array.isArray(rows) && rows.length > 0) {
    const byPlatform = new Map(
      rows
        .filter((row: any) => row?.platform)
        .map((row: any) => [row.platform, row])
    );

    return DEFAULT_APP_SETTINGS.map((defaults) => {
      const existing = byPlatform.get(defaults.platform) || {};
      return {
        label: existing.label || defaults.label,
        platform: defaults.platform,
        version: existing.version || defaults.version,
        appUpdate: !!existing.appUpdate,
        forceUpdate: !!existing.forceUpdate,
        maintenance: !!existing.maintenance,
        storeUrl:
          typeof existing.storeUrl === "string"
            ? existing.storeUrl
            : defaults.storeUrl || "",
        maintenanceMessage:
          typeof existing.maintenanceMessage === "string"
            ? existing.maintenanceMessage
            : defaults.maintenanceMessage || "",
        lastUpdated: existing.lastUpdated || null,
      };
    });
  }

  if (rows && typeof rows === "object") {
    return DEFAULT_APP_SETTINGS.map((defaults) => ({
      ...defaults,
      appUpdate: !!rows.appUpdate,
      forceUpdate: !!rows.forceUpdate,
      maintenance: !!rows.maintenance,
      storeUrl:
        typeof rows.storeUrl === "string"
          ? rows.storeUrl
          : defaults.storeUrl || "",
      maintenanceMessage:
        typeof rows.maintenanceMessage === "string"
          ? rows.maintenanceMessage
          : defaults.maintenanceMessage || "",
    }));
  }

  return DEFAULT_APP_SETTINGS.map((row) => ({ ...row }));
};

const snapshotAppSettings = (rows: AppSettingRow[]) =>
  rows.reduce<Record<string, AppSettingRow>>((acc, row) => {
    acc[row.platform] = { ...row };
    return acc;
  }, {});

const isAppSettingRowChanged = (
  current: AppSettingRow,
  original?: AppSettingRow
) => {
  if (!original) {
    return true;
  }

  return (
    current.label !== original.label ||
    current.version !== original.version ||
    current.appUpdate !== original.appUpdate ||
    current.forceUpdate !== original.forceUpdate ||
    current.maintenance !== original.maintenance
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCms = () => {
  const userData = useAuth();
  const [getAllCms] = useLazyGetCmsQuery();
  const [postCms, { isLoading }] = usePostCmsMutation();
  const [deleteLink] = useDeleteLinksMutation();
  const [addLink] = useAddLinksMutation();
  const [getLinks] = useLazyGetAllLinksQuery();

  const [privacyPolicy, setPrivacyPolicy] = useState<string>("");
  const [privacyPolicyAr, setPrivacyPolicyAr] = useState<string>("");
  const [cmsId, setCmsId] = useState<string>("");
  const [phoneCode, setPhoneCode] = useState("+965");
  const [waPhoneCode, setWaPhoneCode] = useState("+965");
  const [legal, setLegal] = useState<string>("");
  const [legalAr, setLegalAr] = useState<string>("");
  const [agentTermsAndConditions, setAgentTermsAndConditions] =
    useState<string>("");
  const [agentTermsAndConditionsAr, setAgentTermsAndConditionsAr] =
    useState<string>("");
  const [value, setValue] = React.useState(0);
  const [error, setError] = useState(false);
  const [links, setLinks] = useState([]);
  const [page, setPage] = useState(1);
  const [link, setLink] = useState("");
  const [title, setTitle] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [icon, setIcon] = useState("");
  const [iconUploading, setIconUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const translate = useTranslation();
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [hidePermission, setHidePermission] = useState<any>({
    isAdd: true,
    isDelete: true,
  });
  const [appSettings, setAppSettings] =
    useState<AppSettingRow[]>(DEFAULT_APP_SETTINGS);
  const [originalAppSettings, setOriginalAppSettings] = useState<
    Record<string, AppSettingRow>
  >(snapshotAppSettings(DEFAULT_APP_SETTINGS));
  const [savingAppSettingPlatform, setSavingAppSettingPlatform] =
    useState<string>("");

  const getSettingsUpdateMessage = (tabIndex: number) => {
    switch (tabIndex) {
      case 0:
        return translate?.cms?.contactSupportUpdated;
      case 1:
        return translate?.cms?.privacyPolicyUpdated;
      case 2:
        return translate?.cms?.termsUpdated;
      case 3:
        return translate?.cms?.agentTermsUpdated;
      case 4:
        return translate?.cms?.creditPriceUpdated;
      case 6:
        return translate?.cms?.appSettingsUpdated;
      default:
        return translate?.error?.cmsUpdate;
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    setLink("");
    setTitle("");
    setTitleAr("");
    setIcon("");
  };

  const handleIconUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.length ? event.target.files[0] : null;
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!file.type?.startsWith("image/")) {
      showError(translate?.error?.imagesOnly || "");
      return;
    }
    try {
      setIconUploading(true);
      const res = await UploadMedia(file);
      if (res?.statusCode === 200 && res?.data) {
        setIcon(res.data);
      } else {
        showError(res?.message || "");
      }
    } catch (error: any) {
      showError(error?.message || "");
    } finally {
      setIconUploading(false);
    }
  };

  const handleAddLink = async () => {
    if (!title?.trim() || !link?.trim()) {
      showError(translate?.error?.required || "");
      return;
    }
    if (!icon?.trim()) {
      showError(translate?.cms?.linkIconRequired || "");
      return;
    }

    const data = {
      link: link || "",
      title: title || "",
      title_ar: titleAr || "",
      icon: icon || "",
    };

    try {
      let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;
      const response = await addLink(encryptedBody).unwrap();
      if (response?.statusCode === 200) {
        showToast(translate?.cms?.linkAdded || "");
        handleClose();
        getAllLinks();
      }
    } catch (error: any) {
      if (error?.data?.message) {
        showError(error?.data?.message || "");
      }
    }
  };

  const checkPermission = () => {
    const permissions = userData?.permission;
    if (permissions?.length) {
      let idx = -1;
      idx = permissions?.findIndex(
        (ele: any) => ele?.name === "Manage CMS" || ele?.name === "Settings"
      );

      if (idx != -1) {
        let data = {
          isAdd: permissions[idx]?.isAdd,
          isDelete: permissions[idx]?.isDelete,
        };
        setHidePermission(data);
      } else {
        // If subadmin but no permission found, deny access
        setHidePermission({ isAdd: false, isDelete: false });
      }
    }
  };

  const getCmsDetail = async () => {
    try {
      const res = await getAllCms({}).unwrap();
      if (res?.statusCode === 200 && res?.data) {
        formik.setFieldValue("email", res?.data?.contactUs?.email);
        formik.setFieldValue("phone", res?.data?.contactUs?.phone);
        formik.setFieldValue("waPhone", res?.data?.contactUs?.whatsappPhone);
        formik.setFieldValue("normalCred", res?.data?.normalCredPrice);
        formik.setFieldValue("highlightedCred", res?.data?.highlightCredPrice);
        formik.setFieldValue("agentRequestPrice", res?.data?.agentRequestPrice);

        setPrivacyPolicy(res?.data?.privacyPolicy || " ");
        setPrivacyPolicyAr(res?.data?.privacyPolicy_ar || " ");
        setLegal(
          res?.data?.termsAndConditions || res?.data?.legal || " "
        );
        setLegalAr(res?.data?.termsAndConditions_ar || " ");
        setCmsId(res?.data?._id);
        setPhoneCode(res?.data?.contactUs?.dialCode || "");
        setWaPhoneCode(res?.data?.contactUs?.whatsappDialCode || "");
        setAgentTermsAndConditions(res?.data?.agentTermsAndConditions || "");
        setAgentTermsAndConditionsAr(
          res?.data?.agentTermsAndConditions_ar || ""
        );
        const normalizedAppSettings = normalizeAppSettings(
          res?.data?.appSettings
        );
        setAppSettings(normalizedAppSettings);
        setOriginalAppSettings(snapshotAppSettings(normalizedAppSettings));
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteLink({ id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(translate?.cms?.linkDeleted || "");
        getAllLinks();
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const getAllLinks = async () => {
    setLoading(true);
    try {
      const res = await getLinks({}).unwrap();
      if (res?.statusCode === 200) {
        setLoading(false);
        setLinks(res?.data?.data || []);
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    setPhoneCode(country?.dialCode);
  };
  const handleChangeWaPhone = (phone: any, country: any) => {
    formik.setFieldValue("waPhone", phone?.replace(country.dialCode, ""));
    setWaPhoneCode(country?.dialCode);
  };

  const handleAppSettingFieldChange = (
    platform: "ios" | "android",
    field: "version" | "appUpdate" | "forceUpdate" | "maintenance",
    value: string | boolean
  ) => {
    setAppSettings((prev) =>
      prev.map((row) => {
        if (row.platform !== platform) {
          return row;
        }

        const nextRow = { ...row, [field]: value };

        // Force update and soft app update are mutually exclusive.
        if (field === "forceUpdate" && value === true) {
          nextRow.appUpdate = false;
        }
        if (field === "appUpdate" && value === true) {
          nextRow.forceUpdate = false;
        }

        return nextRow;
      })
    );
  };

  const handleSaveAppSettingRow = async (platform: "ios" | "android") => {
    if (!cmsId) {
      return;
    }

    const updatedRows = appSettings.map((row) =>
      row.platform === platform
        ? { ...row, lastUpdated: new Date().toISOString() }
        : row
    );

    try {
      setSavingAppSettingPlatform(platform);
      const encryptedBody = generateEncryptedKeyBody({
        appSettings: updatedRows,
      }) as CommonBody;
      const response = await postCms({
        encryptedBody,
        userId: cmsId,
      }).unwrap();

      if (response?.statusCode === 200) {
        showToast(translate?.cms?.appSettingsUpdated || "");
        setAppSettings(updatedRows);
        setOriginalAppSettings(snapshotAppSettings(updatedRows));
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    } finally {
      setSavingAppSettingPlatform("");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      phone: "",
      waPhone: "",
      normalCred: "",
      highlightedCred: "",
      agentRequestPrice: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Enter a valid email address"
        ),

      phone: Yup.string()
        .required("required")
        .min(6, "Phone number must be at least 6 digits"),
      waPhone: Yup.string()
        .required("Phone number is required")
        .min(6, "Phone number must be at least 6 digits"),
      normalCred: Yup.string().required("required"),
      highlightedCred: Yup.string().required("required"),
      agentRequestPrice: Yup.string().required("required"),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);

      let hasError = false;

      if (
        value === 1 &&
        (!privacyPolicy.trim() || privacyPolicy.trim().length < 8)
      ) {
        setError(true);
        hasError = true;
      } else if (value === 2 && (!legal.trim() || legal.trim().length < 8)) {
        setError(true);
        hasError = true;
      } else if (
        value === 2 &&
        (!legalAr.trim() || legalAr.trim().length < 8)
      ) {
        setError(true);
        hasError = true;
      } else if (
        value === 1 &&
        (!privacyPolicyAr.trim() || privacyPolicyAr.trim().length < 8)
      ) {
        setError(true);
        hasError = true;
      } else {
        setError(false);
      }

      if (hasError) {
        // If there is an error, don't proceed with form submission
        formik.setSubmitting(false);
        return;
      }

      const data = {
        contactUs: {
          email: formik.values.email,
          phone: formik.values.phone,
          dialCode: phoneCode,
          whatsappPhone: formik.values.waPhone,
          whatsappDialCode: waPhoneCode,
        },
        privacyPolicy: privacyPolicy,
        privacyPolicy_ar: privacyPolicyAr,
        termsAndConditions: legal,
        termsAndConditions_ar: legalAr,
        normalCredPrice: formik.values.normalCred,
        highlightCredPrice: formik.values.highlightedCred,
        agentRequestPrice: formik.values.agentRequestPrice,
        agentTermsAndConditions_ar: agentTermsAndConditionsAr,
        agentTermsAndConditions: agentTermsAndConditions,
      };

      console.log(data);

      try {
        let encryptedBody = generateEncryptedKeyBody(data) as CommonBody;
        const response = await postCms({
          encryptedBody,
          userId: cmsId,
        }).unwrap();

        if (response?.statusCode === 200) {
          showToast(getSettingsUpdateMessage(value) || "");
          getCmsDetail();
        }
      } catch (error: any) {
        console.log(error, "error");
        showError(error?.data?.message || "");
      }
    },
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (userData && userData?.permission?.length) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    getCmsDetail();
    getAllLinks();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.main?.ManageSettings}</h1>
        </div>
        <Card className="cards">
          <Box sx={{ px: 2, pt: 2 }}>
            <PublicLinksCard
              title={translate?.cms?.publicLinksTitle || "Public Links"}
              description={
                translate?.cms?.publicLinksDescription ||
                "Use these URLs in App Store Connect and Google Play Console."
              }
              pageLabel={translate?.cms?.publicLinksPage || "Page"}
              urlLabel={translate?.cms?.publicLinksUrl || "Public URL"}
              copyLabel={translate?.cms?.publicLinksCopy || "Copy URL"}
              openLabel={translate?.cms?.publicLinksOpen || "Open in new tab"}
              copiedMessage={translate?.cms?.publicLinksCopied || "URL copied"}
              links={[
                {
                  key: "privacy",
                  label: translate?.main?.privacyPolicy || "Privacy Policy",
                  path: "/privacy-policy",
                },
                {
                  key: "terms",
                  label: translate?.main?.TermsConditions || "Terms & Conditions",
                  path: "/terms-and-conditions",
                },
                {
                  key: "contact",
                  label: translate?.main?.contactSupport || "Contact Support",
                  path: "/contact-us",
                },
              ]}
            />
          </Box>
          <Box className="custom_tabs">
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label={translate?.main?.contactSupport} {...a11yProps(0)} />
              <Tab label={translate?.main?.privacyPolicy} {...a11yProps(1)} />
              <Tab label={translate?.main?.TermsConditions} {...a11yProps(2)} />
              <Tab
                label={translate?.main?.AgentTermsConditions}
                {...a11yProps(3)}
              />
              <Tab label={translate?.main?.CreditPrice} {...a11yProps(4)} />
              <Tab label={translate?.main?.RealEstateLinks} {...a11yProps(5)} />
              <Tab label={translate?.main?.AppSettings} {...a11yProps(6)} />
            </Tabs>
            <CustomTabPanel value={value} index={0}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={4} md={4} sm={4} xs={12}>
                    <Typography className="custom_label">
                      {translate?.main?.Email}
                    </Typography>
                    <TextField
                      hiddenLabel
                      type={"email"}
                      name="email"
                      variant="outlined"
                      className="text_field"
                      fullWidth
                      placeholder="Email"
                      disabled={!hidePermission?.isAdd}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      inputProps={{ maxLength: 50 }}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid item lg={4} md={4} sm={4} xs={12}>
                    <Typography className="custom_label">
                      {translate?.main?.PhoneNumber}
                    </Typography>
                    <PhoneInput
                      country={"us"}
                      disabled={!hidePermission?.isAdd}
                      value={phoneCode + formik.values.phone}
                      placeholder="0 (000) 000-000"
                      enableSearch={true}
                      inputStyle={{ width: "100%" }}
                      onChange={(phone, country) =>
                        handleChangePhone(phone, country)
                      }
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.phone && formik.errors.phone ? (
                      <h6 className="err_msg">
                        {formik.touched.phone && formik.errors.phone}
                      </h6>
                    ) : (
                      ""
                    )}
                  </Grid>
                  <Grid item lg={4} md={4} sm={4} xs={12}>
                    <Typography className="custom_label">
                      {translate?.main?.WhatsappPhoneNumber}
                    </Typography>
                    <PhoneInput
                      country={"us"}
                      disabled={!hidePermission?.isAdd}
                      value={waPhoneCode + formik.values.waPhone}
                      placeholder="0 (000) 000-000"
                      enableSearch={true}
                      inputStyle={{ width: "100%" }}
                      onChange={(phone, country) =>
                        handleChangeWaPhone(phone, country)
                      }
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.waPhone && formik.errors.waPhone ? (
                      <h6 className="err_msg">
                        {formik.touched.waPhone && formik.errors.waPhone}
                      </h6>
                    ) : (
                      ""
                    )}
                  </Grid>
                </Grid>
                <div className="form_btn">
                  {hidePermission?.isAdd ? (
                    <Button
                      size="large"
                      type="submit"
                      className="btn btn_primary"
                    >
                      {translate?.main?.Save}
                    </Button>
                  ) : null}
                </div>
              </form>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <form onSubmit={formik.handleSubmit}>
                <h3>{translate?.main?.PrivacyPolicyEnglish}</h3>
                <EditText
                  content={privacyPolicy}
                  setContent={setPrivacyPolicy}
                  readOnly={!hidePermission?.isAdd}
                />
                {error ? (
                  <h6 className="err_msg">
                    {translate?.main?.Pleaseenterprivacypolicy}
                  </h6>
                ) : (
                  ""
                )}
                <h3>{translate?.main?.PrivacyPolicyArabic}</h3>
                <EditText
                  content={privacyPolicyAr}
                  setContent={setPrivacyPolicyAr}
                  readOnly={!hidePermission?.isAdd}
                  rtl
                />

                <div className="form_btn">
                  {hidePermission?.isAdd ? (
                    <Button
                      size="large"
                      type="submit"
                      className="btn btn_primary"
                    >
                      {translate?.main?.Save}
                    </Button>
                  ) : null}
                </div>
              </form>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
              <form onSubmit={formik.handleSubmit}>
                <h3>{translate?.main?.TermsConditionsEnglish}</h3>
                <EditText content={legal} setContent={setLegal} readOnly={!hidePermission?.isAdd} />
                {error ? (
                  <h6 className="err_msg">
                    {translate?.main?.PleaseenterTermsandConditions}
                  </h6>
                ) : (
                  ""
                )}
                <h3>{translate?.main?.TermsConditionsArabic}</h3>
                <EditText content={legalAr} setContent={setLegalAr} readOnly={!hidePermission?.isAdd} rtl />
                {error ? (
                  <h6 className="err_msg">
                    {translate?.main?.PleaseenterTermsandConditions}
                  </h6>
                ) : (
                  ""
                )}
                <div className="form_btn">
                  {hidePermission?.isAdd ? (
                    <Button
                      size="large"
                      type="submit"
                      className="btn btn_primary"
                    >
                      {translate?.main?.Save}
                    </Button>
                  ) : null}
                </div>
              </form>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={3}>
              <form onSubmit={formik.handleSubmit}>
                <h3>{translate?.main?.TermsConditionsEnglish}</h3>
                <EditText
                  content={agentTermsAndConditions}
                  setContent={setAgentTermsAndConditions}
                  readOnly={!hidePermission?.isAdd}
                />
                {error ? (
                  <h6 className="err_msg">
                    {translate?.main?.PleaseenterTermsandConditions}
                  </h6>
                ) : (
                  ""
                )}
                <h3>{translate?.main?.TermsConditionsArabic}</h3>
                <EditText
                  content={agentTermsAndConditionsAr}
                  setContent={setAgentTermsAndConditionsAr}
                  readOnly={!hidePermission?.isAdd}
                  rtl
                />
                {error ? (
                  <h6 className="err_msg">
                    {translate?.main?.PleaseenterTermsandConditions}
                  </h6>
                ) : (
                  ""
                )}
                <div className="form_btn">
                  {hidePermission?.isAdd ? (
                    <Button
                      size="large"
                      type="submit"
                      className="btn btn_primary"
                    >
                      {translate?.main?.Save}
                    </Button>
                  ) : null}
                </div>
              </form>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={4}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <Typography className="custom_label">
                      {translate?.main?.NormalCredits}
                    </Typography>
                    <TextField
                      hiddenLabel
                      type={"text"}
                      name="normalCred"
                      variant="outlined"
                      className="text_field"
                      fullWidth
                      placeholder="Normal credits"
                      disabled={!hidePermission?.isAdd}
                      onBlur={formik.handleBlur}
                      value={formik.values.normalCred}
                      onChange={formik.handleChange}
                      inputProps={{ maxLength: 50 }}
                      helperText={
                        formik.touched.normalCred && formik.errors.normalCred
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <Typography className="custom_label">
                      {translate?.main?.HighlightedCredits}
                    </Typography>
                    <TextField
                      hiddenLabel
                      type={"text"}
                      name="highlightedCred"
                      variant="outlined"
                      className="text_field"
                      fullWidth
                      placeholder="Highlighted credits"
                      disabled={!hidePermission?.isAdd}
                      onBlur={formik.handleBlur}
                      value={formik.values.highlightedCred}
                      onChange={formik.handleChange}
                      inputProps={{ maxLength: 50 }}
                      helperText={
                        formik.touched.highlightedCred &&
                        formik.errors.highlightedCred
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={12}>
                    <Typography className="custom_label">
                      {translate?.main?.agentRequestPrice}
                    </Typography>
                    <TextField
                      hiddenLabel
                      type={"text"}
                      name="agentRequestPrice"
                      variant="outlined"
                      className="text_field"
                      fullWidth
                      placeholder="Request Price"
                      disabled={!hidePermission?.isAdd}
                      onBlur={formik.handleBlur}
                      value={formik.values.agentRequestPrice}
                      onChange={formik.handleChange}
                      inputProps={{ maxLength: 50 }}
                      helperText={
                        formik.touched.agentRequestPrice &&
                        formik.errors.agentRequestPrice
                      }
                    />
                  </Grid>
                </Grid>
                <div className="form_btn">
                  {hidePermission?.isAdd ? (
                    <Button
                      size="large"
                      type="submit"
                      className="btn btn_primary"
                    >
                      {translate?.main?.Save}
                    </Button>
                  ) : null}
                </div>
              </form>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={5}>
              <Box className="cards_header">
                <div></div>
                <Box className="cards_header_right">
                  {hidePermission?.isAdd ? (
                    <Button
                      className="btn btn_primary"
                      onClick={() => setModalOpen(true)}
                    >
                      Add
                    </Button>
                  ) : null}
                </Box>
              </Box>
              <TableContainer className="table_container">
                <Box className="heading"></Box>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        {translate?.main?.SNo}
                      </TableCell>
                      <TableCell>{translate?.cms?.linkIcon}</TableCell>
                      <TableCell>{translate?.main?.Title}</TableCell>
                      <TableCell>{translate?.main?.Link}</TableCell>
                      {hidePermission?.isDelete ? <TableCell>{translate?.main?.Action}</TableCell> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {links?.length > 0
                      ? links?.map((row: any, i: any) => (
                        <TableRow key={row?._id}>
                          <TableCell align="center">
                            {(page - 1) * 10 + i + 1}
                          </TableCell>
                          <TableCell>
                            {row?.icon ? (
                              <Box
                                component="img"
                                src={resolveMediaUrl(row.icon)}
                                alt={row?.title || "icon"}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  backgroundColor: "#F5F6F8",
                                  padding: "4px",
                                }}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>{row?.title || "-"}</TableCell>
                          <TableCell>{row?.link || "-"}</TableCell>
                          {hidePermission?.isDelete ? (
                            <TableCell>
                              {" "}
                              <IconButton
                                onClick={() => {
                                  setOpen(true);
                                  setSelectedId(row?._id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      ))
                      : ""}
                  </TableBody>
                </Table>
              </TableContainer>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={6}>
              <Box sx={{ py: 2 }}>
                <Typography className="custom_label" sx={{ mb: 1 }}>
                  {translate?.cms?.appSettingsDescription}
                </Typography>
                <Typography
                  sx={{ mb: 2, color: "text.secondary", fontSize: 14 }}
                >
                  {translate?.cms?.appSettingsHint}
                </Typography>
                <TableContainer className="table_container">
                  <Table sx={{ minWidth: 900 }} aria-label="app settings table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate?.main?.Label || "Label"}</TableCell>
                        <TableCell>{translate?.cms?.platformType}</TableCell>
                        <TableCell>{translate?.cms?.version}</TableCell>
                        <TableCell>{translate?.cms?.appUpdate}</TableCell>
                        <TableCell>{translate?.cms?.forceUpdates}</TableCell>
                        <TableCell>{translate?.cms?.maintenanceMode}</TableCell>
                        <TableCell>{translate?.cms?.lastUpdated}</TableCell>
                        <TableCell align="center">
                          {translate?.main?.Action}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {appSettings.map((row) => {
                        const originalRow = originalAppSettings[row.platform];
                        const canSave =
                          isAppSettingRowChanged(row, originalRow) &&
                          !!row.version?.trim();

                        return (
                          <TableRow key={row.platform}>
                            <TableCell>
                              {row.label
                                ? row.label.charAt(0).toUpperCase() +
                                row.label.slice(1)
                                : row.label}
                            </TableCell>
                            <TableCell>{row.platform}</TableCell>
                            <TableCell>
                              <TextField
                                hiddenLabel
                                size="small"
                                value={row.version}
                                disabled={!hidePermission?.isAdd}
                                onChange={(event) =>
                                  handleAppSettingFieldChange(
                                    row.platform,
                                    "version",
                                    event.target.value
                                  )
                                }
                                placeholder="1.0.0"
                              />
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={row.appUpdate}
                                disabled={!hidePermission?.isAdd}
                                onChange={(event) =>
                                  handleAppSettingFieldChange(
                                    row.platform,
                                    "appUpdate",
                                    event.target.checked
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={row.forceUpdate}
                                disabled={!hidePermission?.isAdd}
                                onChange={(event) =>
                                  handleAppSettingFieldChange(
                                    row.platform,
                                    "forceUpdate",
                                    event.target.checked
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={row.maintenance}
                                disabled={!hidePermission?.isAdd}
                                onChange={(event) =>
                                  handleAppSettingFieldChange(
                                    row.platform,
                                    "maintenance",
                                    event.target.checked
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {row.lastUpdated
                                ? moment(row.lastUpdated).format(
                                  "M/D/YYYY, h:mm:ss A"
                                )
                                : "-"}
                            </TableCell>
                            <TableCell align="center">
                              {hidePermission?.isAdd ? (
                                <Button
                                  variant="contained"
                                  className="btn btn_primary sm"
                                  disabled={
                                    !canSave ||
                                    savingAppSettingPlatform === row.platform
                                  }
                                  onClick={() =>
                                    handleSaveAppSettingRow(row.platform)
                                  }
                                >
                                  {translate?.main?.Save}
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CustomTabPanel>
          </Box>
        </Card>

        <Dialog
          open={modalOpen}
          onClose={handleClose}
          className="revert_dialog"
        >
          <DialogContent>
            <Typography className="dialog_title">{translate?.cms?.addLinks}</Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography className="custom_label">
                {translate?.cms?.linkIcon}
              </Typography>
              {icon ? (
                <div className="upload_image_preview category_icon_upload">
                  <CardMedia
                    component="img"
                    image={resolveMediaUrl(icon)}
                    alt="icon"
                    sx={{
                      backgroundColor: "transparent",
                      background: "transparent",
                    }}
                  />
                  <CancelIcon onClick={() => setIcon("")} />
                </div>
              ) : (
                <Box className="upload_image">
                  <label htmlFor="real-estate-link-icon">
                    <Input
                      sx={{ display: "none" }}
                      id="real-estate-link-icon"
                      type="file"
                      inputProps={{
                        accept: "image/png,image/jpeg,image/jpg,image/webp",
                      }}
                      onChange={handleIconUpload}
                    />
                    <Button
                      component="span"
                      className="upload_image_btn category_icon_upload"
                      disabled={iconUploading}
                    >
                      <span className="category_icon_placeholder">
                        <CameraAltIcon />
                      </span>
                    </Button>
                  </label>
                </Box>
              )}
            </FormControl>

            <FormControl fullWidth>
              <Typography className="custom_label">{translate?.cms?.linkTitle}</Typography>
              <TextField
                hiddenLabel
                className="text_field"
                autoFocus
                name="normal"
                type="text"
                placeholder={translate?.cms?.linkTitle || ""}
                fullWidth
                value={title}
                onChange={(val) => {
                  setTitle(val.target.value);
                }}
              />
            </FormControl>

            <FormControl fullWidth style={{ marginTop: 15 }}>
              <Typography className="custom_label">{translate?.cms?.linkTitleAr}</Typography>
              <TextField
                hiddenLabel
                className={withArabicFieldClass("text_field")}
                inputProps={arabicInputProps}
                name="normal"
                type="text"
                placeholder={translate?.cms?.linkTitleAr || ""}
                fullWidth
                value={titleAr}
                onChange={(val) => {
                  setTitleAr(val.target.value);
                }}
              />
            </FormControl>

            <FormControl fullWidth sx={{ paddingTop: 2 }}>
              <Typography className="custom_label">{translate?.cms?.linkUrl}</Typography>
              <TextField
                hiddenLabel
                className="text_field"
                name="highlighted"
                type="text"
                placeholder="Link"
                fullWidth
                value={link}
                onChange={(val) => {
                  setLink(val.target.value);
                }}
              />
            </FormControl>
            <Box className="form_btn">
              <Button className="btn" variant="outlined" onClick={handleClose}>
                {translate?.agents?.Cancel}
              </Button>
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={iconUploading}
                onClick={handleAddLink}
              >
                Add
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        <WarnModal
          open={open}
          setOpen={setOpen}
          handleDelete={() => handleDelete(selectedId)}
          name={"Link"}
        />
      </div>
    </MainContainer>
  );
};

export default ManageCms;
